import React from 'react';
import { Link } from 'react-router-dom';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useCart, MAX_CART_QUANTITY } from '../../context/CartContext';

const Cart = () => {
  const { cartItems, updateQuantity, removeFromCart } = useCart();

  const getItemQuantity = (item) => {
    const parsed = Math.floor(Number(item.quantity));
    if (!Number.isFinite(parsed)) return 1;
    return Math.min(Math.max(parsed, 1), MAX_CART_QUANTITY);
  };

  // Adapted for your cart item structure
  const getItemPrice = (item) =>
    item.product?.isRental ? (item.product?.securityDeposit ?? 0) : (item.product?.price ?? 0);
  const subtotal = cartItems.reduce((total, item) => total + getItemPrice(item) * getItemQuantity(item), 0);
  const shipping = subtotal >= 1999 || subtotal === 0 ? 0 : 120;
  const total = subtotal + shipping;

  const handleQuantityChange = (item, delta) => {
    const newQty = getItemQuantity(item) + delta;
    // Use the trash button to drop a line; quantity itself never goes below one.
    if (newQty < 1 || newQty > MAX_CART_QUANTITY) return;
    updateQuantity(item.product._id, newQty, item.size, item.color);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Shopping Cart</h1>

      {cartItems.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">Your cart is empty</p>
          <Link
            to="/products"
            className="inline-block bg-indigo-600 text-white px-6 py-3 rounded-md hover:bg-indigo-700"
          >
            Continue Shopping
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              {cartItems.map((item) => {
                const isRental = item.product?.isRental;
                const quantity = getItemQuantity(item);
                return (
                  <div key={item._id} className="p-6 border-b border-gray-200 last:border-b-0">
                    <div className="flex items-center">
                      {/* Show rentalImages[0] if images[0] is not present */}
                      <img
                        src={item.product?.images?.[0] || item.product?.rentalImages?.[0] || 'https://via.placeholder.com/100'}
                        alt={item.product?.name || 'Product'}
                        className="w-24 h-24 object-cover rounded-md"
                      />
                      <div className="ml-6 flex-1">
                        <h3 className="text-lg font-medium text-gray-900">{item.product?.name || 'Product'}</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          ₹{getItemPrice(item)}
                          {item.product?.isRental && (
                            <span className="ml-2 text-xs text-pink-600 font-semibold">Deposit</span>
                          )}
                        </p>
                        <div className="mt-2 flex items-center">
                          <button
                            className="text-gray-500 hover:text-gray-700"
                            onClick={() => {
                              // For guest users, use product ID; for logged-in users, use item._id
                              const itemId = item._id || item.product._id || item.product.id;
                              removeFromCart(itemId);
                            }}
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                          <div className="ml-4 flex items-center">
                            <button
                              className="px-2 py-1 border border-gray-300 rounded-l-md disabled:opacity-40 disabled:cursor-not-allowed"
                              onClick={() => handleQuantityChange(item, -1)}
                              disabled={isRental || quantity <= 1}
                              aria-label="Decrease quantity"
                            >
                              -
                            </button>
                            <span className="px-4 py-1 border-t border-b border-gray-300">
                              {isRental ? 1 : quantity}
                            </span>
                            <button
                              className="px-2 py-1 border border-gray-300 rounded-r-md disabled:opacity-40 disabled:cursor-not-allowed"
                              onClick={() => handleQuantityChange(item, 1)}
                              disabled={isRental || quantity >= MAX_CART_QUANTITY}
                              aria-label="Increase quantity"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="ml-6">
                        <p className="text-lg font-medium text-gray-900">
                          ₹{(getItemPrice(item) * quantity).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow-md rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Order Summary</h2>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="text-gray-900">₹{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Shipping</span>
                  <span className="text-gray-900">{shipping === 0 ? 'Free' : `₹${shipping.toFixed(2)}`}</span>
                </div>
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex justify-between">
                    <span className="text-lg font-medium text-gray-900">Total</span>
                    <span className="text-lg font-medium text-gray-900">₹{total.toFixed(2)}</span>
                  </div>
                </div>
                <Link
                  to="/checkout"
                  className="w-full bg-gradient-to-r from-pink-500 to-indigo-500 text-white px-6 py-3 rounded-full font-semibold shadow hover:from-pink-600 hover:to-indigo-600 transition text-center block"
                >
                  Proceed to Checkout
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart; 